import type { AtcVersion } from '@capsio/contracts';
import type { ReactNode } from 'react';
import { authoredByText } from './AtcCardPanels.js';

/**
 * 版本历史。
 *
 * **这不是附赠品。** 没有它，「旧版不覆盖」只是库里的事实，界面上看不出来；
 * 而人看不见历史版本，就不会相信升版是安全的，进而会想办法原地改。
 *
 * 每一版都显示内容指纹的前 12 位：**两版内容一样时指纹相同**，
 * 一眼能看出「这次升版到底改了没有」。
 *
 * 「编写」列是这一版方法谁做的：人（V0）/ AI（V1）。带「回填推断」的
 * 是迁移按当时数据推出来的——推断值必须可识别，否则就是又一批
 * 「看起来是事实」的推断值。
 */
export function AtcVersionHistory({ versions }: { versions: AtcVersion[] }): ReactNode {
  if (versions.length === 0) {
    return (
      <p className="sub-hint">
        这张卡还没有任何版本。<strong>这不该出现</strong>——建卡时首版与卡是同一步落库的，
        看到这句话说明数据有问题，请上报。
      </p>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>版本</th>
            <th>WHAT</th>
            <th>确定性</th>
            <th>触发</th>
            <th>编写</th>
            <th>内容指纹</th>
            <th>提交人</th>
            <th>提交时间</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.atcVersionId}>
              <td>
                v{v.versionNo}
                {v.isCurrent && <span className="chip ok">当前</span>}
              </td>
              <td>{v.what === '' ? <em>（空）</em> : v.what}</td>
              <td>{v.determinism === 'exploratory' ? '探索性' : '确定性'}</td>
              <td>{v.triggerType === 'undetermined' ? '未定' : v.triggerType}</td>
              <td>{authoredByText(v)}</td>
              <td className="mono">{v.contentHash.slice(0, 12)}</td>
              <td>{v.createdBy}</td>
              <td>{new Date(v.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sub-hint">
        旧版<strong>永不覆盖</strong>。历史执行记录锁定它执行时的那一版——
        所以改了今天的定义，不会让上个月的记录跟着变。
      </p>
    </div>
  );
}
